import {
  CommunicationBenefitsType,
  CommunicationDividendsType,
  CommunicationDocumentsStatus,
  CommunicationOperationsType,
  CommunicationOtherLettersType,
  filterPageMessages,
  getRangeLastYear,
  useCommunicationDocumentsFilterLogic
} from 'azshared';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { AppButton } from '../../../../../components/buttons/app-button';
import { AppDateSelect } from '../../../../../components/select/app-date-select';
import { AppSelect } from '../../../../../components/select/app-select';
import './communications-filter.scss';

export const CommunicationsFilter = () => {
  const { formatMessage } = useIntl();
  const [hasAnySelection, setHasAnySelection] = useState(false);

  const {
    filter,
    operations,
    status,
    dividends,
    benefits,
    other,
    handleDateChange,
    handleStatusChange,
    handleSubmitFilter,
    handleResetFilter,
    isDateRangeValid,
    handleCategoryOperationsChange,
    handleCategoryDividendsChange,
    handleCategoryBenefitsChange,
    handleCategoryOtherLettersChange,
    handleSelectAll
  } = useCommunicationDocumentsFilterLogic();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleResetFilter?.();
      }
    },
    [handleResetFilter]
  );

  useEffect(() => {
    const hasSelection = !!(
      (filter?.operations && filter.operations.length > 0) ||
      (filter?.dividends && filter.dividends.length > 0) ||
      (filter?.benefits && filter.benefits.length > 0) ||
      (filter?.other && filter.other.length > 0)
    );
    setHasAnySelection(hasSelection);
  }, [filter]);

  const getPlaceholderText = (key: 'Operation' | 'Dividends' | 'Benefits' | 'Other') => {
    switch (key) {
      case 'Operation':
        return formatMessage(filterPageMessages.headColumnAllLettersConfirmOperation);
      case 'Dividends':
        return formatMessage(filterPageMessages.headColumnAllLettersDividends);
      case 'Benefits':
        return formatMessage(filterPageMessages.headColumnAllWelcomeLetters);
      case 'Other':
        return formatMessage(filterPageMessages.headColumnAllOtherLetters);
    }
  };

  const getPlaceholder = (key: 'Operation' | 'Dividends' | 'Benefits' | 'Other') => {
    if (!hasAnySelection) {
      return getPlaceholderText(key);
    }
    if (
      (key === 'Operation' && (!filter?.operations || filter.operations.length === 0)) ||
      (key === 'Dividends' && (!filter?.dividends || filter.dividends.length === 0)) ||
      (key === 'Benefits' && (!filter?.benefits || filter.benefits.length === 0)) ||
      (key === 'Other' && (!filter?.other || filter.other.length === 0))
    ) {
      switch (key) {
        case 'Operation':
          return 'Nessuna operazione...';
        case 'Dividends':
          return 'Nessun dividendo...';
        case 'Benefits':
          return 'Nessun benefit...';
        case 'Other':
          return 'Nessun altro...';
      }
    }
    return getPlaceholderText(key);
  };

  const handleStatus = useCallback(
    (values: CommunicationDocumentsStatus | CommunicationDocumentsStatus[]) => {
      if (Array.isArray(values)) {
        handleStatusChange(values);
      }
    },
    [handleStatusChange]
  );

  const handleCategoryOperations = useCallback(
    (values: CommunicationOperationsType | CommunicationOperationsType[]) => {
      if (Array.isArray(values)) {
        handleCategoryOperationsChange(values);
      }
    },
    [handleCategoryOperationsChange]
  );

  const handleCategoryDividends = useCallback(
    (values: CommunicationDividendsType | CommunicationDividendsType[]) => {
      if (Array.isArray(values)) {
        handleCategoryDividendsChange(values);
      }
    },
    [handleCategoryDividendsChange]
  );

  const handleCategoryBenefits = useCallback(
    (values: CommunicationBenefitsType | CommunicationBenefitsType[]) => {
      if (Array.isArray(values) && values.length > 0) {
        handleCategoryBenefitsChange(values);
      }
    },
    [handleCategoryBenefitsChange]
  );

  const handleCategoryOtherLetters = useCallback(
    (values: CommunicationOtherLettersType | CommunicationOtherLettersType[]) => {
      if (Array.isArray(values)) {
        handleCategoryOtherLettersChange(values);
      }
    },
    [handleCategoryOtherLettersChange]
  );

  const handleSelectAllStatus = useCallback(() => handleSelectAll('status'), [handleSelectAll]);
  const handleSelectAllOperations = useCallback(() => handleSelectAll('operations'), [handleSelectAll]);
  const handleSelectAllDividends = useCallback(() => handleSelectAll('dividends'), [handleSelectAll]);
  const handleSelectAllBenefits = useCallback(() => handleSelectAll('benefits'), [handleSelectAll]);
  const handleSelectAllOthers = useCallback(() => handleSelectAll('other'), [handleSelectAll]);

  return (
    <div className='communications-filter'>
      <AppSelect
        placeholder={formatMessage(filterPageMessages.headColumnAllLettersConfirmStatus)}
        multiple={true}
        value={filter?.status}
        onChange={handleStatus}
        options={status}
        onSelectAll={handleSelectAllStatus}
      />
      <AppSelect
        placeholder={getPlaceholder('Operation')}
        multiple={true}
        value={filter?.operations}
        onChange={handleCategoryOperations}
        options={operations}
        onSelectAll={handleSelectAllOperations}
      />
      <AppSelect
        placeholder={getPlaceholder('Dividends')}
        multiple={true}
        value={filter?.dividends}
        onChange={handleCategoryDividends}
        options={dividends}
        onSelectAll={handleSelectAllDividends}
      />
      <AppSelect
        placeholder={getPlaceholder('Benefits')}
        multiple={true}
        value={filter?.benefits}
        onChange={handleCategoryBenefits}
        options={benefits}
        onSelectAll={handleSelectAllBenefits}
      />
      <AppSelect
        placeholder={getPlaceholder('Other')}
        multiple={true}
        value={filter?.other}
        onChange={handleCategoryOtherLetters}
        options={other}
        onSelectAll={handleSelectAllOthers}
      />
      <AppDateSelect
        placeholder={formatMessage(filterPageMessages.headColumnSelectTimespan)}
        onChange={handleDateChange}
        value={filter?.rangeDate}
        isValid={isDateRangeValid}
        minDate={getRangeLastYear().from}
      />

      <div className='communications-filter-buttons'>
        <div className='communications-filter-reset-button' onClick={handleResetFilter} onKeyDown={handleKeyDown} tabIndex={0}>
          {formatMessage(filterPageMessages.resetFilter)}
        </div>
        <AppButton label={formatMessage(filterPageMessages.applyFilter)} onClick={handleSubmitFilter} />
      </div>
    </div>
  );
};
